<template>
  <header
    id="navbarSection"
    class="pl-3 pl-lg-5 pr-3 pr-lg-5 py-3"
  >
    <nav
      class="flex-center flex-wrap"
      role="navigation"
    >
      <!-- TinyURL Logo -->
      <router-link
        id="home_header_button_logo"
        class="font-bungee font-weight-normal text-white tracking-wide text-4xl text-no-underline"
        :to="{ name: isPastDueTrial ? 'subscription.past_due' : 'homepage' }"
      >
        TinyURL
      </router-link>

      <!-- Mobile Screen -->
      <div
        v-if="isVisible"
        class="d-flex d-md-none ml-auto"
      >
        <button
          v-if="canCreateTinyurl && !isPastDueTrial"
          data-test-id="nav_create_tinyurl_btn"
          type="button"
          class="btn navigation-link bg-blue rounded font-bungee text-md cursor-pointer"
          :class="{ 'bg-blue-dark': createTinyurlPop }"
          @click="toggle('tinyurl')"
        >
          <i class="fa fa-plus d-inline d-sm-none" />
          <span class="d-none d-sm-inline">Create</span>
        </button>

        <button
          v-if="!isPastDueTrial"
          type="button"
          class="btn btn-outline-light ml-2"
          aria-label="Menu"
          @click.stop.prevent="toggleMobile"
        >
          <i class="fa fa-bars fa-lg" />
        </button>

        <a
          v-if="isPastDueTrial"
          class="btn navigation-link bg-blue rounded text-md cursor-pointer"
          :class="{ disabled: isLoggingOut }"
          href="javascript:void(0)"
          role="menuitem"
          :disabled="isLoggingOut"
          @click="doLogout()"
        >
          {{ $t("user:menu.log_out") }}
          <i
            v-show="isLoggingOut"
            class="fa fa-spinner fa-spin"
          />
        </a>
      </div>

      <!-- Wide Screen -->
      <div
        v-if="isVisible"
        class="d-none d-md-block flex-grow-1"
        style="z-index: 12"
      >
        <div class="flex-center-end flex-wrap">
          <button
            v-if="canCreateTinyurl && !isPastDueTrial"
            data-test-id="nav_create_tinyurl_btn"
            type="button"
            class="btn navigation-link bg-blue rounded font-bungee text-md cursor-pointer"
            :class="{ 'bg-blue-dark': createTinyurlPop }"
            @click="toggle('tinyurl')"
          >
            <i class="fa fa-plus d-inline d-md-none" />
            <span class="d-none d-md-inline">Create</span>
            <span class="d-none d-xl-inline"> TinyURL</span>
          </button>

          <!-- Icon Type Navigations -->
          <div class="flex-center mx-2">
            <router-link
              v-if="!isPastDueTrial"
              v-tooltip="{
                content: 'Help',
                trigger: isOnMobile ? 'click' : 'focus hover',
              }"
              :to="{ name: 'help', params: { keepQuery: true } }"
              class="flex-center-center rounded-circle bg-blue text-white mx-1 home_header_button_help"
              :class="{ 'bg-blue-dark': $route.name == 'help' }"
              style="height: 32px; width: 32px"
              aria-label="Help"
            >
              <i class="fa fa-question" />
            </router-link>

            <router-link
              v-if="isUserLoggedIn && !isPastDueTrial"
              v-tooltip="{
                content: 'Notifications',
                trigger: isOnMobile ? 'click' : 'focus hover',
              }"
              :to="{ name: 'notifications' }"
              class="flex-center-center rounded-circle bg-blue text-white mx-1 _notifications-icon"
              :class="{ 'bg-blue-dark': $route.name == 'notifications' }"
              style="height: 32px; width: 32px"
              aria-label="Notifications"
            >
              <Notifications />
            </router-link>

            <div
              v-show="invitationCount > 0"
              v-tooltip="{
                content: 'Invitations',
                trigger: isOnMobile ? 'click' : 'focus hover',
              }"
              class="flex-center-center rounded-circle bg-blue text-white mx-1 cursor-pointer"
              :class="{ 'bg-blue-dark': invitationPop }"
              style="height: 32px; width: 32px"
              tabindex="0"
              @click="toggle('invitation')"
            >
              <div class="position-relative">
                <i class="fa fa-envelope" />
                <span
                  class="position-absolute badge badge-warning align-top ml-n2 mt-n2"
                >
                  {{ invitationCount }}
                </span>
              </div>
            </div>

            <div
              v-show="shoppingCartQuantity > 0 && !isPastDueTrial"
              v-tooltip="{
                content: 'Cart',
                trigger: isOnMobile ? 'click' : 'focus hover',
              }"
              class="flex-center-center rounded-circle bg-blue text-white mx-1 cursor-pointer"
              :class="{ 'bg-blue-dark': $route.path.includes('shopping-cart') }"
              style="height: 32px; width: 32px"
              tabindex="0"
              @click="redirectToCart"
            >
              <div class="position-relative">
                <i class="fa fa-shopping-cart" />
                <span
                  class="position-absolute badge badge-warning align-top ml-n2 mt-n2"
                >
                  {{ shoppingCartQuantity }}
                </span>
              </div>
            </div>
          </div>

          <!-- Text Type Navigations -->
          <div class="flex-center bg-teal rounded position-relative">
            <router-link
              v-if="!canUseAnalytics"
              id="myurls_link_nav--lg"
              class="navigation-link rounded home_header_button_myurls"
              :to="recentLinksDestination"
              role="menuitem"
            >
              My URLs
            </router-link>

            <router-link
              v-if="!isUserSubscribed"
              class="navigation-link rounded home_header_button_pricing"
              :to="{ name: 'signup.pricing', params: { keepQuery: true } }"
              role="menuitem"
            >
              Plans
            </router-link>

            <router-link
              v-if="!isUserSubscribed"
              class="navigation-link rounded home_header_button_blog"
              :to="{ name: 'blog' }"
              role="menuitem"
            >
              Blog
            </router-link>

            <dropdown
              v-if="!isUserSubscribed"
              :caret="{ enable: false }"
              :position="'right'"
              @away="featuresMenu = false"
            >
              <template #trigger>
                <a
                  tabindex="-1"
                  href="javascript:void(0)"
                  class="navigation-link rounded"
                  :class="{ 'bg-blue-dark': $route.path.includes('features') }"
                >
                  <div class="flex-center-between">
                    <span class="mr-2">Features</span>
                    <i
                      :class="[
                        'fa',
                        { 'fa-caret-up': featuresMenu },
                        { 'fa-caret-down': !featuresMenu },
                      ]"
                      aria-hidden="true"
                    />
                  </div>
                </a>
              </template>
              <dropdown-content
                class="rounded-10px mt-1 py-2"
                max-height="500px"
                max-width="325px"
              >
                <template #content>
                  <dropdown-item
                    v-for="f in featurePages"
                    :key="f.title"
                    class="py-2 px-4"
                    @select="redirectToFeature(f.route)"
                  >
                    {{ f.title }}
                  </dropdown-item>
                </template>
              </dropdown-content>
            </dropdown>

            <!-- only show this once module's finished -->
            <!-- <router-link
              :to="{ name: 'purchase-domains' }"
              class="navigation-link rounded"
              aria-label="Purchase Domains"
            >
              Domains
            </router-link> -->

            <router-link
              v-if="!isUserLoggedIn"
              :to="{ name: 'register' }"
              class="navigation-link rounded home_header_button_signup"
              aria-label="Sign Up"
            >
              Sign Up
            </router-link>

            <router-link
              v-if="!isUserLoggedIn"
              :to="{ name: 'login' }"
              class="navigation-link rounded"
              aria-label="Sign In"
              role="menuitem"
            >
              Sign In
            </router-link>

            <template v-if="isUserLoggedIn">
              <dropdown
                v-if="validOrganizations.length || xUserTeam.defaultOrganization"
                :caret="{ enable: false }"
                :position="'right'"
                @away="organizationsMenu = false"
              >
                <template #trigger>
                  <a
                    data-test-id="nav_orgs_dropdown_btn"
                    href="javascript:void(0)"
                    tabindex="-1"
                    class="navigation-link rounded"
                    :class="{
                      'bg-blue-dark': $route.path.includes('organizations'),
                    }"
                    @click.prevent="toggleOrganizations"
                  >
                    <div
                      v-if="
                        inOrganization &&
                          $route.params.org != 'new' &&
                          organization
                      "
                      v-tooltip="{
                        content:
                          organization.name.length > 10
                            ? organization.name
                            : '',
                        trigger: isOnMobile ? 'click' : 'focus hover',
                      }"
                      class="flex-center-between"
                    >
                      <img
                        class="account-img-inline"
                        :src="organization.photo_url"
                      >
                      <span class="mx-2 text-limit-125">
                        {{ organization.name }}
                      </span>
                      <i
                        :class="[
                          'fa',
                          { 'fa-caret-up': organizationsMenu },
                          { 'fa-caret-down': !organizationsMenu },
                        ]"
                        aria-hidden="true"
                      />
                    </div>
                    <div
                      v-else
                      class="flex-center-between"
                    >
                      <span class="mr-2 text-limit-125">Organizations</span>
                      <i
                        :class="[
                          'fa',
                          { 'fa-caret-up': organizationsMenu },
                          { 'fa-caret-down': !organizationsMenu },
                        ]"
                        aria-hidden="true"
                      />
                    </div>
                  </a>
                </template>
                <dropdown-content
                  class="rounded-10px pt-2"
                  max-height="515px"
                  max-width="325px"
                  min-width="250px"
                >
                  <template
                    v-if="supportTeams"
                    #content-fixed
                  >
                    <dropdown-item
                      data-test-id="nav_orgs_create_org_btn"
                      class="py-2 px-4"
                      @select="redirectToOnboarding"
                    >
                      <div class="flex-center text-blue font-weight-seminormal">
                        <span
                          class="flex-center-center rounded-circle mr-2"
                          style="
                            height: 30px;
                            width: 30px;
                            border: 1px dashed #0980a1;
                          "
                        >
                          <i class="fa-solid fa-plus" />
                        </span>
                        Create Organization
                      </div>
                    </dropdown-item>

                    <hr
                      v-show="validOrganizations.length"
                      class="border-gray-300 mt-1 mb-0"
                    >
                  </template>
                  <template #content>
                    <div
                      class="position-relative overflow-y-a pt-1"
                      :class="[
                        { 'pb-1': !validOrganizations.length },
                        { 'pb-2': validOrganizations.length },
                      ]"
                      style="max-height: 238px"
                    >
                      <dropdown-item
                        v-if="xUserTeam.defaultOrganization"
                        :key="'default_org'"
                        data-test-id="_default_org nav_orgs_org_btn"
                        class="py-2 px-4"
                        @select="gotoAccount"
                      >
                        <div class="flex-center">
                          <div class="flex-center text-break">
                            <img
                              class="rounded-circle mr-2 bg-gray-500"
                              style="height: 30px; width: 30px"
                              :src="xUserTeam.defaultOrganization.photo_url"
                            >
                            {{ xUserTeam.defaultOrganization.name }}
                          </div>
                        </div>
                      </dropdown-item>

                      <dropdown-item
                        v-for="o in validOrganizations"
                        :key="o.id"
                        data-test-id="nav_orgs_org_btn"
                        class="py-2 px-4"
                        :class="{ 'is-archived': isOrganizationArchived(o) }"
                        @select="selectOrganization(o, false)"
                      >
                        <div class="flex-center">
                          <div class="flex-center text-break">
                            <img
                              class="rounded-circle mr-2 bg-gray-500"
                              style="height: 30px; width: 30px"
                              :src="o.photo_url"
                            >
                            {{ o.name }}
                          </div>
                          <span
                            v-if="isOrganizationArchived(o)"
                            class="badge badge-red ml-2"
                          >Archived</span>
                        </div>
                      </dropdown-item>
                    </div>
                  </template>
                </dropdown-content>
              </dropdown>

              <router-link
                v-if="!isPastDueTrial"
                :to="{ name: 'settings' }"
                class="navigation-link rounded _account-menu"
                :class="{
                  'bg-blue-dark':
                    $route.path.includes('settings') &&
                    !$route.path.includes('organizations'),
                }"
                role="menuitem"
              >
                <div class="flex-center-between">
                  <img
                    class="account-img-inline"
                    :alt="`${user.name} Photo`"
                    :src="userPhotoUrl"
                  >
                  <span class="ml-2 text-limit-125">{{ user.name }}</span>
                </div>
              </router-link>

              <a
                v-if="isPastDueTrial"
                class="navigation-link rounded"
                :class="{ disabled: isLoggingOut }"
                href="javascript:void(0)"
                role="menuitem"
                :disabled="isLoggingOut"
                @click="doLogout()"
              >
                {{ $t("user:menu.log_out") }}
                <i
                  v-show="isLoggingOut"
                  class="fa fa-spinner fa-spin"
                />
              </a>

              <router-link
                v-if="isDeveloper"
                :to="{ name: 'kiosk' }"
                class="navigation-link rounded"
                :class="{ 'bg-blue-dark': $route.path.includes('kiosk') }"
                role="menuitem"
              >
                Kiosk
              </router-link>

              <a
                v-if="isImpersonating"
                href="/app/kiosk/user/stop-impersonating"
                class="navigation-link rounded _stop-impersonating"
                role="menuitem"
              >
                Back To My Account
              </a>
            </template>
          </div>
        </div>
      </div>

      <div
        v-if="isExternalPage"
        class="d-flex ml-auto"
      >
        <div class="flex-center-end flex-wrap">
          <div class="flex-center bg-teal rounded position-relative">
            <router-link
              class="navigation-link rounded"
              :to="{ name: 'homepage' }"
              role="menuitem"
            >
              Visit TinyURL {{ innerWidth > breakPoints.sm ? ".com" : "" }}
            </router-link>
          </div>
        </div>
      </div>
    </nav>

    <!-- Mobile Popover -->
    <transition name="pop">
      <Popover2Content
        v-if="mobileMenu"
        @close="closeMobilePop"
      >
        <nav
          class="py-2"
          role="navigation"
        >
          <!-- Profile and Organizations List -->
          <template v-if="isUserLoggedIn">
            <router-link
              class="nav-link-m py-2 px-4"
              :to="{ name: 'settings.profile' }"
              role="menuitem"
              aria-haspopup="true"
            >
              <div
                class="flex-center border rounded py-2 px-3"
                :class="{
                  'bg-blue text-white': !inOrganization,
                  'bg-white text-dark border-gray-500': inOrganization,
                }"
              >
                <img
                  class="img-fluid rounded-circle"
                  :alt="`${user.name} Photo`"
                  :src="userPhotoUrl"
                  style="height: 30px; width: 30px"
                >
                <span class="ml-2 text-clamper-1">
                  {{ user.name }}
                </span>
              </div>
            </router-link>

            <a
              v-if="validOrganizations.length || xUserTeam.defaultOrganization"
              class="nav-link-m py-2 px-4"
              :class="{ active: organizationVisibleMobile }"
              href="javascript:void(0)"
              role="menuitem"
              :aria-expanded="organizationVisibleMobile"
              aria-haspopup="true"
            >
              <div
                data-test-id="nav_orgs_dropdown_btn"
                class="flex-center-between border rounded py-2 px-3"
                :class="{
                  'bg-blue text-white': inOrganization,
                  'bg-white text-dark border-gray-500': !inOrganization,
                }"
                @click="toggleOrganizationMobile"
              >
                <div
                  v-if="inOrganization && organization"
                  class="flex-center overflow-hidden text-nowrap"
                >
                  <img
                    class="img-fluid rounded-circle"
                    :src="organization.photo_url"
                    style="height: 30px; width: 30px"
                  >
                  <span
                    class="ml-2 overflow-hidden text-truncate text-limit-125"
                  >
                    {{ organization.name }}
                  </span>
                </div>
                <span
                  v-else
                  class="text-limit-125"
                >Organizations</span>
                <i
                  :class="[
                    'fa',
                    { 'fa-caret-up': organizationVisibleMobile },
                    { 'fa-caret-down': !organizationVisibleMobile },
                  ]"
                  aria-hidden="true"
                />
              </div>
            </a>

            <transition name="slide">
              <div
                v-if="organizationVisibleMobile"
                class="border border-gray-500 rounded mx-4"
              >
                <button
                  v-if="supportTeams"
                  data-test-id="nav_orgs_create_org_btn"
                  class="nav-link-m py-2 px-3"
                  @click="redirectToOnboarding"
                >
                  <div class="flex-center text-blue font-weight-seminormal">
                    <span
                      class="flex-center-center rounded-circle mr-2"
                      style="
                        height: 30px;
                        width: 30px;
                        border: 1px dashed #0980a1;
                      "
                    >
                      <i class="fa-solid fa-plus" />
                    </span>
                    Create Organization
                  </div>
                </button>

                <button
                  v-if="xUserTeam.defaultOrganization"
                  data-test-id="_default_org nav_orgs_org_btn"
                  class="nav-link-m py-2 px-3"
                  @click="gotoAccount"
                >
                  <div class="flex-center">
                    <div class="flex-center text-break">
                      <img
                        class="rounded-circle mr-2 bg-gray-500"
                        style="height: 30px; width: 30px"
                        :src="xUserTeam.defaultOrganization.photo_url"
                      >
                      <span class="text-left">{{ xUserTeam.defaultOrganization.name }}</span>
                    </div>
                  </div>
                </button>

                <div
                  class="overflow-auto"
                  style="max-height: 140px"
                >
                  <button
                    v-for="o in validOrganizations"
                    :key="o.id"
                    data-test-id="nav_orgs_org_btn"
                    class="nav-link-m py-2 px-3"
                    :class="{ 'is-archived': isOrganizationArchived(o) }"
                    @click="selectOrganization(o, true)"
                  >
                    <div class="flex-center">
                      <div class="flex-center text-break">
                        <img
                          class="rounded-circle mr-2"
                          style="height: 30px; width: 30px"
                          :src="o.photo_url"
                        >
                        <span class="text-left">{{ o.name }}</span>
                      </div>
                      <span
                        v-if="isOrganizationArchived(o)"
                        class="badge badge-red ml-2"
                      >Archived</span>
                    </div>
                  </button>
                </div>
              </div>
            </transition>
          </template>

          <div class="my-2" />

          <template v-if="inOrganization">
            <!-- Notifications -->
            <router-link
              v-if="isUserLoggedIn"
              class="nav-link-m-noactive py-2 px-4"
              :to="{ name: 'notifications' }"
            >
              <Notifications viewport="sm" />
            </router-link>

            <hr class="my-1 mx-4">

            <!-- Shopping Cart -->
            <template v-if="shoppingCartQuantity > 0">
              <div
                class="nav-link-m-noactive py-2 px-4"
                @click="redirectToCart"
              >
                Shopping Cart
                <span class="badge badge-warning align-top">
                  {{ shoppingCartQuantity }}
                </span>
              </div>

              <hr class="my-1 mx-4">
            </template>

            <template v-if="shouldShowOrgSettings">
              <a
                href="javascript:void(0)"
                class="nav-link-m py-2 px-4"
                :class="{ 'router-link-active': isInOrgSettings }"
                @click="redirectToOrgSettings"
              >
                Organization Settings
              </a>

              <hr class="my-1 mx-4">
            </template>

            <router-link
              :to="{
                name: 'org.audit',
                params: { org: organization.slug },
              }"
              class="nav-link-m py-2 px-4"
            >
              Audit Trail
            </router-link>

            <template v-if="isDeveloper">
              <hr class="my-1 mx-4">

              <router-link
                :to="{
                  name: 'org.helpdesk',
                  params: { org: organization.slug },
                }"
                class="nav-link-m py-2 px-4"
              >
                Helpdesk
              </router-link>
            </template>
          </template>

          <template v-else>
            <!-- Dashboard or Home -->
            <template v-if="isUserLoggedIn && !isPastDueTrial">
              <router-link
                class="nav-link-m py-2 px-4"
                :to="{ name: 'homepage' }"
                role="menuitem"
                @click.native="closeMobilePop"
              >
                {{ isUserSubscribed ? "Dashboard" : "Home" }}
              </router-link>

              <hr class="my-1 mx-4">
            </template>

            <!-- My URLs -->
            <template v-if="!canUseAnalytics">
              <router-link
                id="myurls_link_nav--sm"
                class="nav-link-m-noactive py-2 px-4 home_header_button_myurls"
                :to="recentLinksDestination"
                role="menuitem"
              >
                My URLs
              </router-link>
              <hr class="my-1 mx-4">
            </template>

            <!-- Notifications -->
            <template v-if="isUserLoggedIn && !isPastDueTrial">
              <router-link
                class="nav-link-m-noactive py-2 px-4"
                :to="{ name: 'notifications' }"
              >
                <Notifications viewport="sm" />
              </router-link>
              <hr class="my-1 mx-4">
            </template>

            <!-- Shopping Cart -->
            <template v-if="shoppingCartQuantity > 0 && !isPastDueTrial">
              <div
                class="nav-link-m-noactive py-2 px-4"
                @click="redirectToCart"
              >
                Shopping Cart
                <span class="badge badge-warning align-top">
                  {{ shoppingCartQuantity }}
                </span>
              </div>
              <hr class="my-1 mx-4">
            </template>

            <!-- Invitations -->
            <template v-if="invitationCount > 0">
              <div
                class="nav-link-m-noactive py-2 px-4"
                @click="toggle('invitation')"
              >
                Invitations
                <span class="badge badge-warning align-top">
                  {{ invitationCount }}
                </span>
              </div>
              <hr class="my-1 mx-4">
            </template>

            <!-- Plans -->
            <template v-if="!isUserSubscribed">
              <router-link
                class="nav-link-m py-2 px-4 home_header_button_pricing"
                :to="{ name: 'signup.pricing', params: { keepQuery: true } }"
                role="menuitem"
              >
                Plans
              </router-link>
              <hr class="my-1 mx-4">
            </template>

            <!-- Blog -->
            <template v-if="!isUserSubscribed">
              <router-link
                class="nav-link-m py-2 px-4 home_header_button_blog"
                :to="{ name: 'blog' }"
                role="menuitem"
              >
                Blog
              </router-link>
              <hr class="my-1 mx-4">
            </template>

            <!-- Features -->
            <template v-if="!isUserSubscribed">
              <a
                class="nav-link-m py-2 px-4 d-flex justify-content-between home_header_button_features"
                :class="{ active: featuresVisibleMobile }"
                href="/app/features"
                role="menuitem"
                :aria-expanded="featuresVisibleMobile"
                aria-haspopup="true"
                @click.stop.prevent="toggleFeaturesMobile"
              >
                Features
                <i
                  :class="[
                    'fa',
                    { 'fa-caret-up': featuresVisibleMobile },
                    { 'fa-caret-down': !featuresVisibleMobile },
                  ]"
                  aria-hidden="true"
                />
              </a>

              <div class="overflow-y-h">
                <transition name="slide">
                  <div
                    v-if="featuresVisibleMobile"
                    class="features"
                  >
                    <router-link
                      v-for="f in featurePages"
                      :key="f.title"
                      class="nav-link-m py-2 px-5 text-sm"
                      :class="f.class"
                      :aria-label="f.title"
                      :to="f.route"
                      @click.native="closeFeatures"
                    >
                      {{ f.title }}
                    </router-link>
                  </div>
                </transition>
              </div>

              <hr class="my-1 mx-4">
            </template>

            <!-- Settings -->
            <template v-if="isUserLoggedIn">
              <template v-for="rs in regularSettings">
                <template v-if="rs.condition">
                  <router-link
                    :key="rs.name"
                    class="nav-link-m py-2 px-4"
                    :class="rs.class"
                    :to="rs.to"
                  >
                    {{ rs.name }}
                  </router-link>

                  <hr
                    :key="`${rs.name}-key`"
                    class="my-1 mx-4"
                  >
                </template>
              </template>
            </template>

            <!-- Billing -->
            <template v-if="isUserLoggedIn">
              <a
                class="nav-link-m py-2 px-4 d-flex justify-content-between"
                :class="{ active: billingVisibleMobile }"
                href="javascript:void(0)"
                role="menuitem"
                :aria-expanded="billingVisibleMobile"
                aria-haspopup="true"
                @click.stop.prevent="toggleBillingMobile"
              >
                {{ $t("Billing") }}
                <i
                  :class="[
                    'fa',
                    { 'fa-caret-up': billingVisibleMobile },
                    { 'fa-caret-down': !billingVisibleMobile },
                  ]"
                  aria-hidden="true"
                />
              </a>

              <div class="overflow-y-h">
                <transition name="slide">
                  <div
                    v-if="billingVisibleMobile"
                    class="billing"
                  >
                    <template v-for="bs in billingSettings">
                      <router-link
                        v-if="bs.condition"
                        :key="bs.name"
                        class="nav-link-m py-2 px-5"
                        :class="bs.class"
                        :to="bs.to"
                      >
                        <!-- <i class="fa fa-fw fa-1x mr-2" :class="bs.icon"></i> -->
                        {{ bs.name }}
                      </router-link>
                    </template>
                  </div>
                </transition>
              </div>
            </template>

            <!-- Help -->
            <template v-if="!isPastDueTrial">
              <hr class="my-1 mx-4">

              <router-link
                class="nav-link-m py-2 px-4 home_header_button_help"
                :to="{ name: 'help' }"
                role="menuitem"
              >
                Help
              </router-link>
            </template>

            <template v-if="isUserLoggedIn && (isDeveloper || isImpersonating)">
              <hr class="my-1 mx-4">

              <a
                class="nav-link-m py-2 px-4 d-flex justify-content-between"
                :class="{ active: kioskVisibleMobile }"
                href="javascript:void(0)"
                role="menuitem"
                :aria-expanded="kioskVisibleMobile"
                aria-haspopup="true"
                @click.stop.prevent="kioskVisibleMobile = !kioskVisibleMobile"
              >
                {{ $t("Kiosk") }}
                <i
                  :class="[
                    'fa',
                    { 'fa-caret-up': kioskVisibleMobile },
                    { 'fa-caret-down': !kioskVisibleMobile },
                  ]"
                  aria-hidden="true"
                />
              </a>

              <div class="overflow-y-h">
                <transition name="slide">
                  <div
                    v-if="kioskVisibleMobile"
                    class="billing"
                  >
                    <router-link
                      v-for="bs in kioskSettings"
                      :key="bs.name"
                      class="nav-link-m py-2 px-5"
                      :to="{ name: bs.to }"
                    >
                      {{ bs.name }}
                    </router-link>
                  </div>
                </transition>
              </div>
            </template>
          </template>
        </nav>
        <div
          slot="footer"
          class="p-3"
        >
          <template v-if="isUserLoggedIn">
            <template v-if="isImpersonating">
              <a
                href="/app/kiosk/user/stop-impersonating"
                class="btn btn-block btn-blue _stop-impersonating"
                role="button"
              >
                Back To My Account
              </a>
            </template>
            <template v-else>
              <button
                data-test-id="nav_logout_btn"
                type="button"
                class="btn btn-block btn-blue"
                :disabled="isLoggingOut"
                @click="onLogout()"
              >
                {{ $t("user:menu.log_out") }}
                <i
                  v-show="isLoggingOut"
                  class="fa fa-spinner fa-spin"
                />
              </button>
              <router-link
                v-if="!canUseAnalytics"
                :to="{ name: 'settings.subscription' }"
                class="btn btn-block btn-green"
                role="button"
              >
                Upgrade Now
              </router-link>
            </template>
          </template>
          <template v-else>
            <router-link
              :to="{ name: 'register' }"
              class="btn btn-block btn-teal home_header_button_signup"
            >
              Create Account
            </router-link>
            <router-link
              :to="{ name: 'login' }"
              class="btn btn-block btn-green"
            >
              Sign In
            </router-link>
          </template>
        </div>
      </Popover2Content>
    </transition>

    <!-- Invitations Popover -->
    <transition name="pop">
      <Popover2Content
        v-if="invitationPop"
        :size="POPOVER_SIZE.MEDIUM"
        @close="closeInvitationPop"
      >
        <template #title>
          Invitations
        </template>

        <div
          v-for="(i, $i) in invitations"
          :key="i.id"
          class="p-4 d-flex w-100 border-bottom"
          :class="{ 'border-0': $i == invitations.length - 1 }"
        >
          <img
            :src="i.organization.photo_url"
            class="rounded-circle"
            style="height: 50px; width: 50px; object-fit: cover"
          >
          <div class="flex-grow-1 pl-3">
            <div class="mb-2 flex-center-between">
              <p class="mb-0 font-weight-semibold text-break">
                {{ i.organization.name }}
              </p>
              <p class="mb-0">
                {{ toRelativeTime(i.updated_at) }}
              </p>
            </div>

            <template v-if="i.source == 'organization'">
              <p class="mb-0 text-break">
                <span class="font-weight-semibold">{{ i.inviter.name }}</span>
                has invited you to join the
                <span class="font-weight-semibold">{{
                  i.organization.name
                }}</span>
                organization as an
                <span class="font-weight-semibold">Organization {{ i.role.name }}</span>.
              </p>
              <div class="py-1" />
              <div
                v-if="i.message"
                class="bg-gray-100 border font-montserrat rounded-xl text-break text-sm mb-2 pt-0 px-3 pb-3"
                style="white-space: pre-line"
              >
                {{ i.message }}
              </div>
            </template>

            <template v-if="i.source == 'team'">
              <p class="mb-0 text-break">
                <span class="font-weight-semibold">{{ i.inviter.name }}</span>
                has invited you to join the
                <span class="font-weight-semibold">{{ i.team.name }}</span>
                team of the
                <span class="font-weight-semibold">{{
                  i.organization.name
                }}</span>
                organization as a
                <span class="font-weight-semibold">Team {{ startCase(i.role) }}</span>.
              </p>
              <div class="py-1" />
            </template>

            <button
              class="btn btn-blue rounded-xl my-1"
              :disabled="invitationsProcessing.includes(`${i.id}-accept`)"
              @click="processInvitation(i, 'accept')"
            >
              Accept
              <i
                v-if="invitationsProcessing.includes(`${i.id}-accept`)"
                class="fa fa-spinner fa-spin"
              />
            </button>
            <button
              class="btn btn-blue rounded-xl my-1"
              :disabled="invitationsProcessing.includes(`${i.id}-reject`)"
              @click="processInvitation(i, 'reject')"
            >
              Reject
              <i
                v-if="invitationsProcessing.includes(`${i.id}-reject`)"
                class="fa fa-spinner fa-spin"
              />
            </button>
          </div>
        </div>

        <div
          v-if="!invitations.length"
          class="h-100 w-100 flex-center-center"
        >
          <div class="text-center">
            <svg
              width="135"
              height="175"
              viewBox="0 0 128 128"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M107.52 109.518C106.043 111.057 104.269 112.281 102.306 113.117C100.343 113.952 98.2312 114.381 96.0977 114.378H18.5977C16.4647 114.381 14.353 113.952 12.3903 113.117C10.4276 112.282 8.65438 111.057 7.17773 109.518L39.0627 74.543L44.6977 80.7205C46.3014 82.48 48.2545 83.8855 50.4323 84.8472C52.6101 85.8089 54.9646 86.3057 57.3452 86.3057C59.7259 86.3057 62.0804 85.8089 64.2582 84.8472C66.4359 83.8855 68.3891 82.48 69.9927 80.7205L75.6277 74.543L107.52 109.518Z"
                fill="#087DA8"
              />
              <path
                d="M89.8722 58.922L69.9997 80.722C68.3958 82.4816 66.4424 83.8872 64.2645 84.8489C62.0865 85.8107 59.7318 86.3074 57.3509 86.3074C54.9701 86.3074 52.6154 85.8107 50.4374 84.8489C48.2594 83.8872 46.3061 82.4816 44.7022 80.722L7.17969 39.567C8.65642 38.0278 10.4296 36.8037 12.3923 35.9684C14.3551 35.1331 16.4666 34.704 18.5997 34.707H80.6397C79.5195 39.2031 79.804 43.9344 81.4547 48.2639C83.1054 52.5933 86.0431 56.313 89.8722 58.922Z"
                fill="#087DA8"
              />
              <path
                d="M96.0975 115.88H18.6C14.0003 115.874 9.59074 114.044 6.33827 110.791C3.08581 107.539 1.25595 103.129 1.25 98.5295V77.977C1.25 77.5792 1.40804 77.1977 1.68934 76.9164C1.97064 76.6351 2.35218 76.477 2.75 76.477C3.14782 76.477 3.52936 76.6351 3.81066 76.9164C4.09196 77.1977 4.25 77.5792 4.25 77.977V98.5295C4.25463 102.334 5.76799 105.981 8.45813 108.671C11.1483 111.362 14.7956 112.875 18.6 112.88H96.1C99.9044 112.875 103.552 111.362 106.242 108.671C108.932 105.981 110.445 102.334 110.45 98.5295V63.0295C106.814 64.2727 102.934 64.6309 99.1325 64.0743C95.3307 63.5177 91.7163 62.0625 88.5895 59.8293C85.4627 57.5961 82.9135 54.6494 81.1535 51.2338C79.3936 47.8181 78.4736 44.0319 78.47 40.1895C78.4703 38.8494 78.5807 37.5116 78.8 36.1895H18.6C14.7925 36.1942 11.1426 37.7099 8.45195 40.4039C5.76131 43.0978 4.25 46.7496 4.25 50.557V66.277C4.25 66.6749 4.09196 67.0564 3.81066 67.3377C3.52936 67.619 3.14782 67.777 2.75 67.777C2.35218 67.777 1.97064 67.619 1.68934 67.3377C1.40804 67.0564 1.25 66.6749 1.25 66.277V50.557C1.25529 45.9572 3.08493 41.5472 6.33754 38.2946C9.59015 35.042 14.0001 33.2123 18.6 33.207H80.64C80.868 33.2067 81.0931 33.2584 81.2981 33.3582C81.5031 33.4579 81.6827 33.6031 81.8232 33.7826C81.9638 33.9622 82.0615 34.1714 82.109 34.3944C82.1565 34.6174 82.1526 34.8483 82.0975 35.0695C81.1342 38.9251 81.2769 42.974 82.509 46.7522C83.7412 50.5304 86.013 53.885 89.064 56.4314C92.1151 58.9778 95.8219 60.6129 99.7596 61.1494C103.697 61.6858 107.706 61.1019 111.327 59.4645C111.556 59.3608 111.807 59.316 112.057 59.3344C112.308 59.3528 112.55 59.4338 112.761 59.5699C112.972 59.706 113.145 59.8928 113.265 60.1134C113.385 60.3339 113.448 60.581 113.448 60.832V98.5295C113.442 103.129 111.613 107.539 108.36 110.792C105.107 114.045 100.697 115.874 96.0975 115.88Z"
                fill="#343A40"
              />
              <path
                d="M2.75 74.75C2.35218 74.75 1.97064 74.592 1.68934 74.3107C1.40804 74.0294 1.25 73.6478 1.25 73.25V71C1.25 70.6022 1.40804 70.2206 1.68934 69.9393C1.97064 69.658 2.35218 69.5 2.75 69.5C3.14782 69.5 3.52936 69.658 3.81066 69.9393C4.09196 70.2206 4.25 70.6022 4.25 71V73.25C4.25 73.6478 4.09196 74.0294 3.81066 74.3107C3.52936 74.592 3.14782 74.75 2.75 74.75Z"
                fill="#343A40"
              />
              <path
                d="M96.0972 115.877H18.5972C16.2616 115.885 13.9488 115.418 11.7998 114.503C9.65074 113.589 7.71045 112.246 6.09718 110.557C5.83365 110.282 5.68444 109.918 5.6798 109.537C5.67516 109.156 5.81543 108.788 6.07218 108.507L37.9572 73.5322C38.0978 73.378 38.269 73.2547 38.4599 73.1704C38.6508 73.0861 38.8572 73.0425 39.0659 73.0425C39.2746 73.0425 39.4811 73.0861 39.672 73.1704C39.8629 73.2547 40.0341 73.378 40.1747 73.5322L45.8072 79.7097C47.2701 81.3157 49.0521 82.5987 51.0392 83.4765C53.0263 84.3544 55.1748 84.8078 57.3472 84.8078C59.5196 84.8078 61.668 84.3544 63.6552 83.4765C65.6423 82.5987 67.4243 81.3157 68.8872 79.7097L74.5197 73.5322C74.6595 73.3759 74.8305 73.2507 75.0217 73.1646C75.2129 73.0784 75.42 73.0334 75.6297 73.0322C75.8389 73.0337 76.0456 73.0789 76.2363 73.1651C76.4271 73.2512 76.5977 73.3762 76.7372 73.5322L108.627 108.5C108.884 108.781 109.023 109.149 109.018 109.53C109.013 109.911 108.864 110.275 108.6 110.55C106.987 112.24 105.046 113.585 102.897 114.5C100.747 115.416 98.4337 115.885 96.0972 115.877ZM9.28218 109.44C11.8732 111.671 15.1828 112.892 18.6022 112.877H96.1022C99.5221 112.891 102.832 111.67 105.425 109.44L75.6322 76.7697L71.1047 81.7322C69.3608 83.6443 67.2372 85.1717 64.8696 86.2167C62.5021 87.2617 59.9426 87.8015 57.3547 87.8015C54.7667 87.8015 52.2073 87.2617 49.8397 86.2167C47.4722 85.1717 45.3486 83.6443 43.6047 81.7322L39.0797 76.7722L9.28218 109.44Z"
                fill="#343A40"
              />
              <path
                d="M57.3504 87.8072C54.7626 87.8032 52.2038 87.2611 49.8367 86.2153C47.4696 85.1695 45.3459 83.6428 43.6004 81.7322L6.07292 40.5772C5.8165 40.2958 5.67667 39.9274 5.68178 39.5467C5.68688 39.1661 5.83654 38.8016 6.10042 38.5272C7.71315 36.8376 9.65333 35.4944 11.8025 34.5798C13.9517 33.6651 16.2647 33.198 18.6004 33.2072H80.6404C80.8684 33.2069 81.0935 33.2585 81.2985 33.3583C81.5036 33.458 81.6832 33.6032 81.8237 33.7827C81.9642 33.9623 82.0619 34.1715 82.1094 34.3945C82.157 34.6175 82.153 34.8484 82.0979 35.0697C81.05 39.268 81.3145 43.6867 82.8558 47.73C84.3971 51.7733 87.1412 55.2468 90.7179 57.6822C90.8955 57.803 91.045 57.9606 91.1564 58.1443C91.2678 58.3279 91.3385 58.5334 91.3636 58.7467C91.3887 58.9601 91.3677 59.1763 91.3021 59.3808C91.2364 59.5853 91.1275 59.7733 90.9829 59.9322L71.1079 81.7322C69.3616 83.6437 67.2367 85.1709 64.8682 86.2168C62.4997 87.2627 59.9395 87.8043 57.3504 87.8072ZM9.28042 39.6422L45.8104 79.7097C47.2733 81.3156 49.0553 82.5986 51.0424 83.4765C53.0296 84.3543 55.178 84.8077 57.3504 84.8077C59.5228 84.8077 61.6713 84.3543 63.6584 83.4765C65.6455 82.5986 67.4275 81.3156 68.8904 79.7097L87.6404 59.1422C84.7801 56.883 82.4686 54.0053 80.8795 50.725C79.2905 47.4447 78.4651 43.8471 78.4654 40.2022C78.4656 38.862 78.576 37.5242 78.7954 36.2022H18.6004C15.181 36.1899 11.8719 37.4113 9.28042 39.6422Z"
                fill="#343A40"
              />
              <path
                d="M102.611 64.3425C97.8363 64.3425 93.169 62.9267 89.1992 60.2742C85.2294 57.6217 82.1354 53.8515 80.3083 49.4405C78.4812 45.0295 78.0031 40.1757 78.9346 35.493C79.866 30.8103 82.1651 26.509 85.5412 23.133C88.9172 19.7569 93.2185 17.4578 97.9012 16.5264C102.584 15.5949 107.438 16.073 111.849 17.9001C116.26 19.7272 120.03 22.8212 122.682 26.791C125.335 30.7608 126.751 35.4281 126.751 40.2025C126.743 46.6024 124.197 52.7379 119.672 57.2633C115.146 61.7887 109.011 64.3346 102.611 64.3425ZM102.611 19.0625C98.4296 19.0625 94.3424 20.3024 90.866 22.6252C87.3895 24.9481 84.6799 28.2498 83.0799 32.1126C81.4799 35.9754 81.0612 40.226 81.8769 44.3267C82.6926 48.4275 84.706 52.1943 87.6625 55.1507C90.619 58.1072 94.3857 60.1206 98.4865 60.9363C102.587 61.752 106.838 61.3334 110.701 59.7333C114.563 58.1333 117.865 55.4237 120.188 51.9473C122.511 48.4708 123.751 44.3836 123.751 40.2025C123.744 34.5979 121.515 29.2247 117.552 25.2616C113.589 21.2985 108.215 19.0691 102.611 19.0625Z"
                fill="#343A40"
              />
              <path
                d="M102.609 49.5154C102.212 49.5154 101.83 49.3574 101.549 49.0761C101.267 48.7947 101.109 48.4132 101.109 48.0154V25.0879C101.109 24.6901 101.267 24.3085 101.549 24.0272C101.83 23.7459 102.212 23.5879 102.609 23.5879C103.007 23.5879 103.389 23.7459 103.67 24.0272C103.951 24.3085 104.109 24.6901 104.109 25.0879V48.0154C104.109 48.4132 103.951 48.7947 103.67 49.0761C103.389 49.3574 103.007 49.5154 102.609 49.5154Z"
                fill="#087DA8"
              />
              <path
                d="M102.609 56.8175C102.212 56.8175 101.83 56.6595 101.549 56.3782C101.267 56.0969 101.109 55.7153 101.109 55.3175V53.75C101.109 53.3522 101.267 52.9706 101.549 52.6893C101.83 52.408 102.212 52.25 102.609 52.25C103.007 52.25 103.389 52.408 103.67 52.6893C103.951 52.9706 104.109 53.3522 104.109 53.75V55.32C104.109 55.7174 103.95 56.0983 103.669 56.379C103.388 56.6598 103.007 56.8175 102.609 56.8175Z"
                fill="#087DA8"
              />
            </svg>
            <br>
            <h4>No Invitations</h4>
          </div>
        </div>
      </Popover2Content>
    </transition>

    <!-- Create TinyURL Popover -->
    <transition name="pop">
      <Component
        :is="createFormComponent.popover"
        v-if="createTinyurlPop"
        data-test-id="create_tinyurl_sidebar"
        @close="closeCreateTinyurlPop"
      >
        <template #title>
          {{ createFormComponent.title }}
        </template>

        <Component :is="createFormComponent.form" />
      </Component>
    </transition>
  </header>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { mixin as clickaway } from "vue-clickaway";
import { startCase } from "lodash-es";
import { isNullish } from "@tinyurldev/tu-ui-vue";

import MixinBfcm from "@Mixins/bfcm2023";
import MixinBillable from "@Mixins/billable";
import MixinMenus from "@Mixins/menus";
import MixinOrganization from "@Mixins/organizations";
import MixinOrganizationPermissions from "@Mixins/organizations/permissions";
import MixinSubscription from "@Mixins/subscriptions";
import MixinBreakpoint from "@Mixins/bootstrap/breakpoint";

import Popover2Content from "@Popover/2/Content.vue";
import PopoverOverhauling from "@Popover/PopoverOverhauling.vue";
import POPOVER_SIZE from "@Constants/popover-size";

import CreateFormLegacy from "@Components/layouts/homepage/CreateForm.vue";
import CreateFormOverhaul from "@Components/tinyurl/shortener/Index.vue";
import Notifications from "@Components/main/Notifications.vue";

import Confirm from "@Modals/Confirm.vue";
import RestoreOrganization from "@Modals/RestoreOrganization.vue";

import useAuth from "@Composables/useAuth";
import useNotify from "@Composables/useNotify";

import { toRelativeTime } from "@Helpers/support";

import { XTeamsStore } from "@Resources/js/modules/teams/Store/XTeamsStore";
import { XUserTeamStore } from "@Modules/teams/Store/XUserTeamStore";
import { XShoppingCartStore } from "@Modules/shoppingcart/Store/XShoppingCartStore";
import { XTeamInvitationsStore } from "@Modules/teams/Store/XTeamInvitationsStore";
import { XOrganizationInvitationsStore } from "@Modules/teams/Store/XOrganizationInvitationsStore";
import { logError, logInfo } from "@Resources/js/helpers/logger";

export default {
  name: "NavigationMenu",

  components: { Popover2Content, Notifications },

  mixins: [
    clickaway,
    MixinBfcm,
    MixinBillable,
    MixinMenus,
    MixinOrganization,
    MixinOrganizationPermissions,
    MixinSubscription,
    MixinBreakpoint,
  ],

  props: {
    isExternalPage: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { notifyError, notifySuccess } = useNotify();
    const { isLoggingOut, doLogout } = useAuth();
    const teamsStore = XTeamsStore();
    const xUserTeam = XUserTeamStore();
    const cartStore = XShoppingCartStore();
    const teamInvitationsStore = XTeamInvitationsStore();
    const organizationInvitationsStore = XOrganizationInvitationsStore();

    return {
      POPOVER_SIZE,
      toRelativeTime,
      startCase,
      notifyError,
      notifySuccess,
      isLoggingOut,
      doLogout,
      teamsStore,
      xUserTeam,
      cartStore,
      teamInvitationsStore,
      organizationInvitationsStore,
    };
  },

  data: () => ({
    organizationsMenu: false,
    featuresMenu: false,
    mobileMenu: false,

    invitationPop: false,
    createTinyurlPop: false,

    featuresVisibleMobile: false,
    settingsVisibleMobile: false,
    billingVisibleMobile: false,
    organizationVisibleMobile: false,

    invitationsProcessing: [],

    kioskVisibleMobile: false,
  }),

  computed: {
    _: () => _,


    ...mapGetters([
      "isAccountUsageEnabled",
      "isOnMobile",
      "user",
      "isUserLoggedIn",
      "isDeveloper",
      "isImpersonating",
      "isUserSubscribed",
      "supportTeams",
      "canUseAnalytics",
      "isOverhaulEnabled",
    ]),

    shoppingCartQuantity() {
      return this.cartStore.quantity;
    },

    createFormComponent() {
      const isOverhauledVersionEnabled = this.isDeveloper && !this.inOrganization;

      if (isOverhauledVersionEnabled) {
        return {
          title: "Shorten a Link",
          popover: PopoverOverhauling,
          form: CreateFormOverhaul,
        };
      }

      return {
        title: "Create TinyURL Link",
        popover: Popover2Content,
        form: CreateFormLegacy,
      };
    },

    recentLinksDestination() {
      if (this.isOverhaulEnabled) {
        return {
          name: "homepage",
          hash: "#recent-links",
        };
      }

      return { name: "my-urls" };
    },

    isVisible() {
      return !this.inBfcmLandingPage && !this.isExternalPage;
    },

    invitations() {
      return [
        ...this.teamInvitationsStore.invitations,
        ...this.organizationInvitationsStore.invitations,
      ];
    },

    invitationCount() {
      return this.invitations.length;
    },

    userPhotoUrl() {
      return _.get(this.user, "photo_url", "/images/user.svg");
    },

    validOrganizations() {
      const defaultOrg = this.xUserTeam.defaultOrganization;
      return Object.values(this.teamsStore.organizations)
        .filter((o) => {
          if (!defaultOrg) {
            return true;
          }
          
          return (o.slug !== defaultOrg.slug);
        })
        .sort((a, b) => {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();

          return aName < bName ? -1 : aName > bName ? 1 : 0;
        });
    },

    shouldShowAudit() {
      if (this.isUserAuthorized("audit")) {
        return true;
      } else {
        if (this.isOrgOwner) {
          return true;
        } else {
          return false;
        }
      }
    },
  },

  watch: {
    $route() {
      this.closeMenus();
    },
  },

  created() {
    Bus.$on("toggleMobileMenu", () => (this.mobileMenu = !this.mobileMenu));
    Bus.$on("toggleTinyurl", () => this.toggle("tinyurl"));

    /**
     * @remarks TODO: Should be removed as soon as legacy experience
     * is replaced by site redesign.
     */
    this.$watch(
      () => this.$route.params.openCreate,
      (shouldOpenCreateForm) => {
        if (shouldOpenCreateForm) {
          this.createTinyurlPop = true;
        }
      },
      { "immediate": true },
    );
  },

  mounted() {
    this._keyListener = function (e) {
      if (e.repeat) return;

      if (e.key === "Escape") {
        e.preventDefault(); // present "Save Page" from getting triggered.
        Bus.$emit("closePopover1");
        Bus.$emit("closePopover2");
      }
    };

    document.addEventListener("keydown", this._keyListener.bind(this));
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this._keyListener);
    Bus.$off("toggleMobileMenu");
    Bus.$off("toggleTinyurl");
  },

  methods: {
    ...mapMutations([
      "SET_TINYURLS_FILTERS_SEARCH",
    ]),

    /**
     * We need to go to the users account dashboard. this method is used to decide whether we should send
     * the user to the legacy application or to the new application
     */ 
    gotoAccount() {
        const defaultOrg = this.xUserTeam.defaultOrganization;
            
        console.log("Navigating to user's account", defaultOrg, defaultOrg.teams);
        console.log("Teams allowed ", this.xUserTeam.isTeamsAllowed);

        /**
         * If the user has a default team then we redirect to that 
         */ 
        if(defaultOrg && this.xUserTeam.isTeamsAllowed) {
          defaultOrg.visit();
        }

         /**
          * Othereise we redirect to the legacy application 
          */ 
        else {
          console.log("user does not have a default org or team. redirecting to legacy page");
          this.$router.push({ name: "settings" });
        }
    },
    toggleFeatures() {
      this.featuresMenu = !this.featuresMenu;
    },
    toggleOrganizations() {
      this.organizationsMenu = !this.organizationsMenu;
    },
    toggleMobile() {
      this.mobileMenu = !this.mobileMenu;
      this.featuresVisibleMobile = false;
      this.settingsVisibleMobile = false;
      this.billingVisibleMobile = false;

      if (this.mobileMenu && this.$route.meta.type) {
        if (_.includes(this.$route.meta.type, "features")) {
          this.featuresVisibleMobile = true;
        } else if (_.includes(this.$route.meta.type, "settings")) {
          this.settingsVisibleMobile = true;
        } else if (_.includes(this.$route.meta.type, "billing")) {
          this.billingVisibleMobile = true;
        }
      }
    },

    toggle(type) {
      if (type == "invitation") {
        this.invitationPop = !this.invitationPop;
      }

      if (type == "tinyurl") {
        if (!this.isAccountUsageEnabled) {
          this.createTinyurlPop = !this.createTinyurlPop;
          return;
        }

        if (
          this.inOrganization ||
          !this.hasLimit("aliases_per_billing_month")
        ) {
          this.createTinyurlPop = !this.createTinyurlPop;
        } else {
          if (
            this.canOverage("aliases_per_billing_month") &&
            this.isOverageOn("aliases_per_billing_month")
          ) {
            this.createTinyurlPop = !this.createTinyurlPop;
          } else {
            this.createTinyurlPop = !this.createTinyurlPop;
          }
        }
      }
    },

    closeFeatures() {
      this.featuresMenu = false;
    },
    closeMobilePop() {
      this.mobileMenu = false;
    },
    closeMenus() {
      this.featuresMenu = false;
      this.mobileMenu = false;
    },
    closeInvitationPop() {
      this.invitationPop = false;
    },
    closeCreateTinyurlPop() {
      this.createTinyurlPop = false;
    },

    toggleFeaturesMobile() {
      this.featuresVisibleMobile = !this.featuresVisibleMobile;
      this.settingsVisibleMobile = false;
      this.billingVisibleMobile = false;
      this.organizationVisibleMobile = false;
    },
    toggleSettingsMobile() {
      this.featuresVisibleMobile = false;
      this.settingsVisibleMobile = !this.settingsVisibleMobile;
      this.billingVisibleMobile = false;
      this.organizationVisibleMobile = false;
    },
    toggleBillingMobile() {
      this.featuresVisibleMobile = false;
      this.settingsVisibleMobile = false;
      this.billingVisibleMobile = !this.billingVisibleMobile;
      this.organizationVisibleMobile = false;
    },
    toggleOrganizationMobile() {
      this.featuresVisibleMobile = false;
      this.settingsVisibleMobile = false;
      this.billingVisibleMobile = false;
      this.organizationVisibleMobile = !this.organizationVisibleMobile;
    },

    redirectToCart() {
      if (this.$route.name == "purchase.domain") {
        this.$router.push({ name: "purchase.domain.cart" }).catch(() => {});
      } else {
        this.$router
          .push({
            name: "shopping-cart",
            params: { keepQuery: true },
          })
          .catch(() => {});
      }
    },

    redirectToOnboarding() {
      this.organizationsMenu = false;

      this.$router
        .push({ name: "org.onboarding" })
        .then(() => {
          Bus.$emit("resetOnboarding");
        })
        .catch(() => {});
    },

    redirectToOrganization(organization) {
      if (this.isOrganizationArchived(organization)) {
        Bus.$emit("showModal", RestoreOrganization, {
          organization,
          callback: (response) => {
            if (response) {
              this.unarchiveOrg({
                params: { org: organization.slug },
                options: {
                  callback: () => {
                    this.UPDATE_STATE_ORGANIZATION_STATUS({
                      ...organization,
                      status: { archived: false },
                    });

                    this.refreshState();
                    this.notifySuccess({
                      text: "The organization has been restored.",
                    });
                  },
                },
              });
            }
          },
        });

        return;
      }

      this.SET_TINYURLS_FILTERS_SEARCH({
        query: "",
        type: "alias",
      });

      organization.visit();
    },
    redirectToFeature(f) {
      this.$router.push(f).catch(() => {});
      this.closeFeatures();
    },

    async processInvitation(i, m) {
      this.invitationsProcessing.push(`${i.id}-${m}`);

      const store =
        i.source == "team"
          ? this.teamInvitationsStore
          : this.organizationInvitationsStore;

      try {
        if (m == "accept") {
          await store.acceptInvitation(i.id);
        } else {
          await store.rejectInvitation(i.id);
        }

        this.notifySuccess({ text: `The invitation has been ${m}ed.` });
      } catch (e) {
        const message =
          e?.response?.data?.errors?.invitation?.[0] ||
          "Something went wrong. Please try again.";
        this.notifyError({ text: message });
      } finally {
        this.invitationsProcessing = this.invitationsProcessing.filter(
          (p) => p != `${i.id}-${m}`,
        );
      }
    },
    selectOrganization(o, isMobile) {
      this.redirectToOrganization(o);

      if (isMobile) {
        this.toggleOrganizationMobile();
      } else {
        this.organizationsMenu = false;
      }
    },
    isOrganizationArchived(o) {
      return o.status?.archived;
    },
    onLogout() {
      this.doLogout();
      this.closeMobilePop();
    },
  },
};
</script>

<style lang="scss" scoped>
.is-archived {
  filter: grayscale(0.75);
  background-color: rgba(0, 0, 0, 0.25);

  &:hover {
    filter: grayscale(0.25);
    background-color: rgba(0, 0, 0, 0.15);
  }
}

.text-limit-125 {
  max-width: 125px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
